import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ModeProvider } from "@/context/ModeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { InstallPrompt } from "@/components/InstallPrompt";
import { NotificationWatcher } from "@/components/NotificationWatcher";
import { PermissionsPrompt } from "@/components/PermissionsPrompt";
import { ProJobAlert } from "@/components/ProJobAlert";
import { Splash } from "@/components/Splash";
import { ConsentGate } from "@/components/ConsentGate";
import { JoinResume } from "@/components/JoinResume";

import Home from "@/pages/Home";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Notifications from "@/pages/Notifications";
import MyRefunds from "@/pages/MyRefunds";
import Helpdesk from "@/pages/Helpdesk";
import AdminSupport from "@/pages/admin/AdminSupport";
import CareWallet from "@/pages/care/CareWallet";
import CareBookings from "@/pages/care/CareBookings";
import BookingFlow from "@/pages/BookingFlow";
import Bookings from "@/pages/Bookings";
import BookingDetail from "@/pages/BookingDetail";
import MyHome from "@/pages/MyHome";
import HomePassport from "@/pages/HomePassport";
import MyTeam from "@/pages/MyTeam";
import MyRequests from "@/pages/MyRequests";
import MasterBookingDetail from "@/pages/MasterBookingDetail";
import Profile from "@/pages/Profile";
import Referral from "@/pages/Referral";
import SocietyJoin from "@/pages/SocietyJoin";
import SocietyPoster from "@/pages/admin/SocietyPoster";
import MySociety from "@/pages/MySociety";
import AdminSafety from "@/pages/admin/AdminSafety";
import CareHome from "@/pages/care/CareHome";
import CarePlans from "@/pages/care/CarePlans";
import CareHealth from "@/pages/care/CareHealth";
import CareVisit from "@/pages/care/CareVisit";
import CareFamily from "@/pages/care/CareFamily";

import ProJobs from "@/pages/pro/ProJobs";
import ProApplication from "@/pages/pro/ProApplication";
import JoinPro from "@/pages/pro/JoinPro";
import Legal from "@/pages/Legal";
import LegalPolicy from "@/pages/LegalPolicy";
import Payments from "@/pages/Payments";
import AdminLegal from "@/pages/admin/AdminLegal";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminOffers from "@/pages/admin/AdminOffers";
import ProJobDetail from "@/pages/pro/ProJobDetail";
import ProEarnings from "@/pages/pro/ProEarnings";
import ProReferral from "@/pages/pro/ProReferral";
import ProAccount from "@/pages/pro/ProAccount";

import { AdminLayout } from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminProfessionals from "@/pages/admin/AdminProfessionals";
import AdminCarePayouts from "@/pages/admin/AdminCarePayouts";
import AdminAccountClosures from "@/pages/admin/AdminAccountClosures";
import AdminServices from "@/pages/admin/AdminServices";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminVendors from "@/pages/admin/AdminVendors";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminTeamInbox from "@/pages/admin/AdminTeamInbox";
import AdminHealthcare from "@/pages/admin/AdminHealthcare";
import HealthcareBook from "@/pages/care/HealthcareBook";
import HealthcareBookings from "@/pages/care/HealthcareBookings";
import HealthcareBookingDetail from "@/pages/care/HealthcareBookingDetail";
import PatientProfile from "@/pages/care/PatientProfile";
import HealthRecords from "@/pages/care/HealthRecords";
import ReportDetail from "@/pages/care/ReportDetail";
import Medications from "@/pages/care/Medications";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminComplaints from "@/pages/admin/AdminComplaints";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminDemand from "@/pages/admin/AdminDemand";
import AdminSocietyHomeCare from "@/pages/admin/AdminSocietyHomeCare";
import AdminCareVisits from "@/pages/admin/AdminCareVisits";
import AdminCare from "@/pages/admin/AdminCare";
import AdminEmergencies from "@/pages/admin/AdminEmergencies";

const cust = (el) => <ProtectedRoute role="customer">{el}</ProtectedRoute>;
const pro = (el) => <ProtectedRoute role="professional">{el}</ProtectedRoute>;
const admin = (el) => <ProtectedRoute role="admin">{el}</ProtectedRoute>;

// Intercept the Google OAuth redirect ({origin}/#session_id=...) BEFORE normal routing.
function GoogleAuthGate({ children }) {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return children;
}

function App() {
    return (
        <AuthProvider>
            <LanguageProvider>
            <ModeProvider>
            <Toaster position="top-center" richColors />
            <Splash />
            <InstallPrompt />
            <NotificationWatcher />
            <PermissionsPrompt />
            <BrowserRouter>
                <GoogleAuthGate>
                <ConsentGate />
                <JoinResume />
                <ProJobAlert />
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/join-pro" element={<JoinPro />} />
                    <Route path="/society/:code" element={<SocietyJoin />} />
                    <Route path="/admin-poster/society/:sid" element={admin(<SocietyPoster />)} />
                    <Route path="/my-society" element={cust(<MySociety />)} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/legal/:slug" element={<LegalPolicy />} />

                    <Route path="/book" element={cust(<BookingFlow />)} />
                    <Route path="/bookings" element={cust(<Bookings />)} />
                    <Route path="/payments" element={cust(<Payments />)} />
                    <Route path="/bookings/master/:id" element={cust(<MasterBookingDetail />)} />
                    <Route path="/bookings/:id" element={cust(<BookingDetail />)} />
                    <Route path="/my-home" element={cust(<MyHome />)} />
                    <Route path="/my-home/:homeId" element={cust(<HomePassport />)} />
                    <Route path="/my-team" element={cust(<MyTeam />)} />
                    <Route path="/my-requests" element={cust(<MyRequests />)} />
                    <Route path="/profile" element={cust(<Profile />)} />
                    <Route path="/referral" element={cust(<Referral />)} />
                    <Route path="/notifications" element={cust(<Notifications />)} />
                    <Route path="/refunds" element={cust(<MyRefunds />)} />
                    <Route path="/help" element={cust(<Helpdesk />)} />
                    <Route path="/pro/help" element={pro(<Helpdesk />)} />
                    <Route path="/wallet" element={cust(<CareWallet />)} />
                    <Route path="/care/bookings" element={cust(<CareBookings />)} />

                    <Route path="/care" element={cust(<CareHome />)} />
                    <Route path="/care/plans" element={cust(<CarePlans />)} />
                    <Route path="/care/health" element={cust(<CareHealth />)} />
                    <Route path="/care/healthcare" element={cust(<HealthcareBook />)} />
                    <Route path="/care/healthcare/bookings" element={cust(<HealthcareBookings />)} />
                    <Route path="/care/healthcare/bookings/:id" element={cust(<HealthcareBookingDetail />)} />
                    <Route path="/care/patients/:memberId" element={cust(<PatientProfile />)} />
                    <Route path="/care/patients/:memberId/records" element={cust(<HealthRecords />)} />
                    <Route path="/care/patients/:memberId/medications" element={cust(<Medications />)} />
                    <Route path="/care/reports/:rid" element={cust(<ReportDetail />)} />
                    <Route path="/care/healthcare/:kind" element={cust(<HealthcareBook />)} />
                    <Route path="/care/book" element={cust(<CareVisit />)} />
                    <Route path="/care/family" element={cust(<CareFamily />)} />


                    <Route path="/pro" element={pro(<ProJobs />)} />
                    <Route path="/pro/application" element={pro(<ProApplication />)} />
                    <Route path="/pro/jobs/:id" element={pro(<ProJobDetail />)} />
                    <Route path="/pro/earnings" element={pro(<ProEarnings />)} />
                    <Route path="/pro/referral" element={pro(<ProReferral />)} />
                    <Route path="/pro/account" element={pro(<ProAccount />)} />

                    <Route path="/admin" element={admin(<AdminLayout />)}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="bookings" element={<AdminBookings />} />
                        <Route path="bookings/:id" element={<AdminBookingDetail />} />
                        <Route path="customers" element={<AdminCustomers />} />
                        <Route path="demand" element={<AdminDemand />} />
                        <Route path="society-homecare" element={<AdminSocietyHomeCare />} />
                        <Route path="professionals" element={<AdminProfessionals />} />
                        <Route path="caregivers" element={<AdminProfessionals care />} />
                        <Route path="care-payouts" element={<AdminCarePayouts />} />
                        <Route path="account-closures" element={<AdminAccountClosures />} />
                        <Route path="care-visits" element={<AdminCareVisits />} />
                        <Route path="care" element={<AdminCare />} />
                        <Route path="emergencies" element={<AdminEmergencies />} />
                        <Route path="services" element={<AdminServices />} />
                        <Route path="payments" element={<AdminPayments />} />
                        <Route path="vendors" element={<AdminVendors />} />
                        <Route path="team" element={<AdminTeam />} />
                        <Route path="team-inbox" element={<AdminTeamInbox />} />
                        <Route path="healthcare" element={<AdminHealthcare />} />
                        <Route path="refunds" element={<AdminRefunds />} />
                        <Route path="support" element={<AdminSupport />} />
                        <Route path="complaints" element={<AdminComplaints />} />
                        <Route path="referrals" element={<AdminReferrals />} />
                        <Route path="safety" element={<AdminSafety />} />
                        <Route path="offers" element={<AdminOffers />} />
                        <Route path="legal" element={<AdminLegal />} />
                        <Route path="audit" element={<AdminAudit />} />
                        <Route path="settings" element={<AdminSettings />} />
                    </Route>
                </Routes>
                </GoogleAuthGate>
            </BrowserRouter>
            </ModeProvider>
            </LanguageProvider>
        </AuthProvider>
    );
}

export default App;
